import React from "react";
import { useNavigate } from "react-router-dom";
import { createRoom, getBedsList, getRoomSizeType } from "adminApi/RoomApi/Room";
import { RoomRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import AddRoomForm from "./Components/AddRoomForm";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import { validateForm } from "utils/validation";
import fields from "../json/RoomFields.json";
import useFetchAllData from "Hooks/useFetchAllData";

const buildActiveRules = (values) => {
    const type = values.accommodation_type;
    const active = {
        accommodation_type: RoomRules.accommodation_type,
        room_type: RoomRules.room_type,
        room_size: RoomRules.room_size,
        room_status: RoomRules.room_status,
        beds_no: RoomRules.beds_no,
        beds_type: RoomRules.beds_type,
    };

    if (type === "ROOM") {
        return {
            ...active,
            capacity: RoomRules.capacity,
            exbed_capability: RoomRules.exbed_capability,
            ...(values.exbed_capability === "1" && {
                exbed_capability_limit: RoomRules.exbed_capability_limit,
            }),
            rate: RoomRules.rate,
            bed_charge: RoomRules.bed_charge,
            person_charge: RoomRules.person_charge,
            number_of_star: RoomRules.number_of_star,
        };
    }

    if (type === "DORMITORY") {
        return {
            ...active,
            rate_per_bed: RoomRules.rate_per_bed,
        };
    }

    return { accommodation_type: RoomRules.accommodation_type };
};

const sanitizePayload = (values) => {
    const type = values.accommodation_type;
    const payload = { accommodation_type: type };

    // Shared
    payload.room_size = values.room_size;
    payload.room_size_mesurement = values.room_size_mesurement;
    payload.room_description = values.room_description;
    payload.reserve_condition = values.reserve_condition;
    payload.room_status = values.room_status;
    payload.room_active = values.room_active || "1";
    payload.beds_no = values.beds_no;
    payload.beds_type = values.beds_type;
    payload.room_type = values.room_type;

    if (type === "ROOM") {
        payload.capacity = values.capacity;
        payload.exbed_capability = values.exbed_capability;
        payload.exbed_capability_limit = String(values.exbed_capability) === "1"
            ? (values.exbed_capability_limit !== "" && values.exbed_capability_limit != null ? values.exbed_capability_limit : null)
            : null;
        payload.rate = values.rate;
        payload.bed_charge = values.bed_charge;
        payload.person_charge = values.person_charge;
        payload.number_of_star = values.number_of_star;
        payload.rate_per_bed = null;
    }

    if (type === "DORMITORY") {
        payload.capacity = values.beds_no || null;
        payload.rate_per_bed = values.rate_per_bed;
        payload.exbed_capability = null;
        payload.exbed_capability_limit = null;
        payload.rate = null;
        payload.bed_charge = null;
        payload.person_charge = null;
        payload.number_of_star = null;
    }

    return payload;
};

const AddRoom = () => {
    const navigate = useNavigate();
    const { formdata } = fields;

    const initialValues = formdata.reduce(
        (acc, field) => ({ ...acc, [field.name]: "" }),
        {}
    );

    const { values, errors, handleChange, setValues, setErrors } = useFormChange(initialValues, RoomRules);

    const handleChangeWithSync = (e) => {
        handleChange(e);
        if (e.target.name === "beds_no" && values.accommodation_type === "DORMITORY") {
            setValues((prev) => ({ ...prev, capacity: e.target.value }));
        }
    };

    const { data: roomTypeOptions, loading: loadingRoomType } = useFetchAllData(getRoomSizeType);
    const { data: bedTypeOptions, loading: loadingBedType } = useFetchAllData(getBedsList);

    const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
        async (formValues) => await createRoom({ data: formValues }),
        (vals) => {
            const activeRules = buildActiveRules(vals);
            const err = validateForm(vals, activeRules);
            setErrors(err);
            return err;
        },
        () => navigate(-1),
        "Room Created Successfully"
    );

    return (
        <>
            {(loadingSubmit || loadingRoomType || loadingBedType) && <Spinner isSpinner={true} />}
            <AddRoomForm
                fields={formdata}
                values={values}
                errors={errors}
                handleChange={handleChangeWithSync}
                handleSubmit={(e) => handleSubmit(e, sanitizePayload(values))}
                loading={loadingSubmit}
                roomTypeOptions={roomTypeOptions || []}
                bedTypeOptions={bedTypeOptions || []}
            />
        </>
    );
};

export default AddRoom;
